import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { CrmObject } from '@/types/crm';

import type { HighlightDefinition } from '../registry/entityTypeRegistry';
import { HighlightCardShell } from './HighlightCardShell';
import { iconForAssociation } from './highlightIcons';
import { useHighlightAssociationCount } from './useHighlightAssociationCount';

interface AssociationCountHighlightCardProps {
  object: CrmObject;
  definition: Extract<HighlightDefinition, { kind: 'association_count' }>;
  orgId: number;
}

export function AssociationCountHighlightCard({
  object,
  definition,
  orgId,
}: AssociationCountHighlightCardProps) {
  const result = useHighlightAssociationCount(orgId, object, definition);
  const [, setSearchParams] = useSearchParams();

  // When the definition links to a tab, the tile switches the full-page
  // record's active tab (e.g. Contacts count → Contacts tab) via `?tab=`.
  const { linkToTab } = definition;
  const handleNavigateToTab = useCallback(() => {
    if (!linkToTab) {
      return;
    }
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.set('tab', linkToTab);
        return next;
      },
      { replace: true }
    );
  }, [linkToTab, setSearchParams]);

  return (
    <HighlightCardShell
      label={definition.label}
      result={result}
      icon={iconForAssociation(definition.associationType)}
      onClick={linkToTab ? handleNavigateToTab : null}
      actionLabel={linkToTab ? `View ${definition.label}` : undefined}
    />
  );
}
